import {MinusIcon, PlusIcon} from '@heroicons/react/outline';
import {useState} from 'react';

import {Button, Table, TBody, Td, Th, Thead, Tr} from '@/components';
import type {AdminAfriexPerson, AfriexAdminTransaction} from '@/types';
import {capitalizeWords, formatTxChannel} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';

const RewardTransactionTable = ({
  items,
  user,
}: {
  items: AfriexAdminTransaction[];
  user: AdminAfriexPerson;
}) => {
  return (
    <div className="w-full">
      <div className="mb-5 flex gap-5">
        <div className="flex flex-col bg-white dark:bg-gray-900 p-3 w-48 h-20 gap-2 border border-gray-300 rounded-md cursor-pointer dark:border-dark-borderHeavy">
          <h3 className="text-sm text-gray-500 dark:text-gray-300">Tier</h3>
          <p className="text-xl text-gray-700 font-semibold dark:text-gray-400">
            Member
          </p>
        </div>
        <div className="flex flex-col bg-white dark:bg-gray-900 p-3 w-48 h-20 gap-2 border border-gray-300 rounded-md cursor-pointer dark:border-dark-borderHeavy">
          <h3 className="text-sm text-gray-500 dark:text-gray-200">
            Reward Points
          </h3>
          <p className="text-xl text-gray-700 font-semibold dark:text-gray-100">
            {user?.rewardInfo?.rewardPoints?.toFixed(2) || 0}
          </p>
        </div>
      </div>

      {items.length ? (
        <Table>
          <Thead>
            <Tr enableHover={false}>
              <Th className="w-20">&nbsp;</Th>
              <Th className="py-4 px-2 text-left w-24">Amount</Th>
              <Th className="py-4 px-2 text-left w-20">Type</Th>
              <Th className="py-4 px-2 text-left w-24">Channel</Th>
              <Th className="py-4 px-2 text-left w-20">Points</Th>
              <Th className="py-4 px-2 text-left w-32">Date</Th>
            </Tr>
          </Thead>

          <TBody>
            {items.map((item, index) => (
              <TRow key={index} item={item} index={index} />
            ))}
          </TBody>
        </Table>
      ) : (
        <div className="flex items-center gap-1 justify-center mt-5">
          <p className="text-gray-800">No reward transactions found.</p>
        </div>
      )}
    </div>
  );
};

const TRow = ({item, index}: {item: AfriexAdminTransaction; index: number}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const textClassName = 'text-sm text-gray-800';

  const amount = item?.destinationAmount;
  const asset = item?.destinationAsset;
  const type = capitalizeWords(item?.type || 'N/A');
  const date = formatDate.DDMonYYYY(item?.createdAt?.toString());
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';

  return (
    <>
      <Tr className={zebraRow}>
        <Td isNumeric>
          <Button onClick={() => setIsExpanded(!isExpanded)} variant="unStyled">
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight dark:text-gray-100 text-gray-700 w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                isExpanded && 'bg-cyan-50'
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4" />
              ) : (
                <PlusIcon className="w-4 h-4" />
              )}
            </div>
          </Button>
        </Td>
        <Td
          className={`py-4 text-left text-sm font-medium text-red-500 dark:text-red-500`}
        >
          {`-${amount?.toLocaleString()} ${asset}`}
        </Td>

        <Td className={`py-4 text-left ${textClassName} capitalize`}>{type}</Td>

        <Td className={`py-4 text-left ${textClassName}`}>
          {formatTxChannel(item)}
        </Td>

        <Td
          className={`py-4 text-left ${textClassName} text-green-500 dark:text-green-500`}
        >
          {`+${item?.meta?.rewardPoints?.toLocaleString() || 0} pts`}
        </Td>

        <Td className={`py-4 text-left ${textClassName}`}>{date}</Td>
      </Tr>

      {isExpanded && (
        <Tr enableHover={false}>
          <Td colSpan={8} className="p-0 m-0">
            <div className="flex py-10 px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
              <Summary data={item} />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

const Summary = ({data}: {data: AfriexAdminTransaction}) => {
  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg w-full px-5 sm:px-10 py-5 sm:py-10">
      <DetailTableHeader data={data} />
      <DetailTable data={data} />
    </div>
  );
};

const DetailTableHeader = ({data}: {data: AfriexAdminTransaction}) => {
  return (
    <>
      <div className="w-full flex justify-between items-center font-semibold">
        <div className="text-cyan-700 uppercase">
          {data.destinationAccountDetails?.accountName || 'N/A'}
        </div>
      </div>
      <hr className="border-1 border-gray-200 mt-2 mb-5 w-full dark:border-dark-borderMid" />
    </>
  );
};

const DetailTable = ({data}: {data: AfriexAdminTransaction}) => {
  const summary = {
    Amount: data.destinationAmount,
    Asset: data.destinationAsset,
    'Transaction Type': capitalizeWords(data.type || ''),
    Channel: formatTxChannel(data),
    'Account Name': data.destinationAccountDetails?.accountName,
    'Reward Points': data?.meta?.rewardPoints || 0,
  };

  return (
    <div className="w-full">
      {Object.entries(summary).map(([key, value], idx) => (
        <div
          key={idx}
          className="flex justify-between py-2 border-b last:border-0 dark:border-dark-borderMid"
        >
          <p className="text-slate-500 dark:text-slate-100 text-sm">{key}</p>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-100 w-1/2 break-all text-right">
            {value}
          </p>
        </div>
      ))}
    </div>
  );
};

export default RewardTransactionTable;
